import { Injectable } from '@angular/core';
import { filter, Observable, switchMap, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Coupon, MenuItem, CartTotals, CouponValidation, Store as StoreModule } from '@core/models';
import { environment } from '@environments/environment';
import { IResponseObjectSuccess, IResponseSuccess } from '@core/interfaces';
import { selectCoupon, selectCouponList, selectStore } from '@store/selectors';
import { v4 as uuidv4 } from 'uuid';
import { IntegerPrecisionService } from '@core/services/integer-precision/integer-precision.service';

@Injectable({
	providedIn: 'root',
})
export class CouponService {
	coupon$: Observable<Coupon | null>;
	couponList$: Observable<Coupon[] | null>;
	store$: Observable<StoreModule | null>;

	constructor(
		private http: HttpClient,
		private store: Store,
		private integerPrecisionService: IntegerPrecisionService
	) {
		this.coupon$ = this.store.select(selectCoupon);
		this.couponList$ = this.store.select(selectCouponList);
		this.store$ = this.store.select(selectStore);
	}

	findAll(): Observable<IResponseSuccess<Coupon>> {
		return this.store$.pipe(
			filter(store => !!store),
			switchMap(store => {
				return this.http.get<IResponseSuccess<Coupon>>(`${environment.microPerk}/cupon`, {
					headers: {
						authorization: store.apiKey,
					},
					params: {
						localId: store.idLocal,
					},
				});
			}),
			take(1)
		);
	}

	applyCoupon(
		coupon: string,
		localChannelId: string | null
	): Observable<IResponseObjectSuccess<{ couponList: Coupon[] }>> {
		return this.store$.pipe(
			take(1),
			filter(store => !!store),
			switchMap(store => {
				return this.http.post<IResponseObjectSuccess<{ couponList: Coupon[] }>>(
					`${environment.microPerk}/cupon/aplicar`,
					{
						code: coupon,
						localId: store?.idLocal as string,
						localCanalId: localChannelId as string,
					},
					{
						headers: {
							authorization: store?.apiKey as string,
						},
					}
				);
			})
		);
	}

	validCoupon(
		coupon: Coupon,
		localChannelId: string | null,
		cartList: MenuItem[],
		cartTotals: CartTotals
	): Observable<IResponseObjectSuccess<CouponValidation>> {
		return this.store$.pipe(
			take(1),
			filter(store => !!store),
			switchMap(store => {
				return this.http.post<IResponseObjectSuccess<CouponValidation>>(
					`${environment.microPerk}/cupon/validate`,
					{
						localId: store?.idLocal as string,
						localCanalId: localChannelId as string,
						code: coupon.code,
						precisionDecimal: this.integerPrecisionService.internalPrecision(),
						value: Number(cartTotals.total),
						articleList: cartList.map(item => ({
							id: item.idArticulo,
							detailId: uuidv4(),
							qty: Number(this.integerPrecisionService.changePrecisionToInternal(item?.cantidad ?? 1n, 0)),
							price: Number(item?.precio ?? 0n),
						})),
					},
					{
						headers: {
							authorization: store?.apiKey as string,
						},
					}
				);
			})
		);
	}
}
